import { useLayoutEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import Popover from '@mui/material/Popover';
import InputBase from '@mui/material/InputBase';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import Button from '@components/Button';
import { SelectableChip } from '@components/Chip';
import { usePopupZIndex } from '@components/Modal/popupLayer';
import { Typography } from '@components/Typography';
import { SectionTitle, Hint } from '@features/ChannelsForms/FormSection';
import { useTranslation } from '@hooks';
import { MAX_ALLOWED_SITES, isValidHostname, normalizeAllowedSite } from '@constants/liveChat';

const ChipsRow = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
});

const AddRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '4px 12px',
});

const enteredSites = value =>
    value
        .split(',')
        .map(raw => ({ raw: raw.trim(), site: normalizeAllowedSite(raw) }))
        .filter(entry => entry.raw);

const SiteChips = ({ sites, onChange }) => {
    const { t } = useTranslation();
    const popupZIndex = usePopupZIndex();
    const [anchorEl, setAnchorEl] = useState(null);
    const [draft, setDraft] = useState('');
    const [invalid, setInvalid] = useState(false);
    const popoverActions = useRef(null);

    useLayoutEffect(() => {
        popoverActions.current?.updatePosition();
    }, [sites.length]);

    const closeAdd = () => {
        setAnchorEl(null);
        setDraft('');
        setInvalid(false);
    };

    const commitDraft = ({ discardInvalid = false, keepOpen = false } = {}) => {
        const entered = enteredSites(draft);
        const accepted = entered.filter(entry => isValidHostname(entry.site)).map(entry => entry.site);
        const rejected = entered.filter(entry => !isValidHostname(entry.site)).map(entry => entry.raw);
        const next = [...new Set([...sites, ...accepted])].slice(0, MAX_ALLOWED_SITES);
        if (next.length !== sites.length) onChange(next);
        if (next.length >= MAX_ALLOWED_SITES) {
            closeAdd();
            return;
        }
        if (rejected.length > 0 && !discardInvalid) {
            setDraft(rejected.join(', '));
            setInvalid(true);
            return;
        }
        if (keepOpen) {
            setDraft('');
            return;
        }
        closeAdd();
    };

    const onDraftKeyDown = event => {
        if (event.key !== 'Enter' && event.key !== ',') return;
        event.preventDefault();
        commitDraft({ keepOpen: event.key === ',' });
    };

    const onDraftPaste = event => {
        const pasted = event.clipboardData?.getData('text') ?? '';
        if (!/[\r\n]/.test(pasted)) return;
        event.preventDefault();
        const { value, selectionStart, selectionEnd } = event.target;
        const lines = pasted
            .split(/[\r\n]+/)
            .map(line => line.trim())
            .filter(Boolean)
            .join(', ');
        setDraft(
            `${value.slice(0, selectionStart ?? value.length)}${lines}${value.slice(selectionEnd ?? value.length)}`,
        );
        setInvalid(false);
    };

    return (
        <>
            <ChipsRow>
                {sites.map(site => (
                    <SelectableChip
                        key={site}
                        label={site}
                        onDelete={() => onChange(sites.filter(item => item !== site))}
                    />
                ))}
                {sites.length < MAX_ALLOWED_SITES ? (
                    <Button
                        type="button"
                        variant="text"
                        sx={{ height: 30, minWidth: 0, padding: '4px 8px' }}
                        onClick={event => setAnchorEl(event.currentTarget)}
                    >
                        <AddRoundedIcon sx={{ fontSize: 16 }} />
                        {t('buttons.add')}
                    </Button>
                ) : null}
            </ChipsRow>
            <Popover
                action={popoverActions}
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={(event, reason) =>
                    reason === 'escapeKeyDown' ? closeAdd() : commitDraft({ discardInvalid: true })
                }
                sx={{ zIndex: popupZIndex, '.MuiPopover-paper': { borderRadius: '8px', minWidth: 260 } }}
            >
                <AddRow>
                    <InputBase
                        autoFocus
                        placeholder="example.com"
                        value={draft}
                        error={invalid}
                        inputProps={{ 'aria-label': t('SettingsChannels.allowedSites') }}
                        onChange={event => {
                            setDraft(event.target.value);
                            setInvalid(false);
                        }}
                        onKeyDown={onDraftKeyDown}
                        onPaste={onDraftPaste}
                        sx={{ fontSize: 14, flex: 1 }}
                    />
                    <Button
                        type="button"
                        variant="text"
                        sx={{ height: 24, minWidth: 0, padding: '4px 8px', whiteSpace: 'nowrap' }}
                        disabled={!draft.trim()}
                        onClick={() => commitDraft()}
                    >
                        {t('buttons.add')}
                    </Button>
                </AddRow>
                {invalid ? (
                    <Typography
                        variant="caption"
                        role="alert"
                        sx={{ color: 'var(--error-main)', display: 'block', padding: '0 12px 8px' }}
                    >
                        {t('SettingsChannels.allowedSiteInvalid')}
                    </Typography>
                ) : null}
            </Popover>
        </>
    );
};

const AllowedSites = () => {
    const { control } = useFormContext();
    const { t } = useTranslation();

    return (
        <>
            <SectionTitle sx={{ marginBottom: '8px' }}>{t('SettingsChannels.allowedSites')}</SectionTitle>
            <Hint sx={{ marginBottom: '8px' }}>{t('SettingsChannels.allowedSitesHint')}</Hint>
            <Controller
                control={control}
                name="allowedSites"
                render={({ field }) => <SiteChips sites={field.value || []} onChange={field.onChange} />}
            />
        </>
    );
};

export default AllowedSites;
